<template>
  <div
    class="tw-grid tw-grid-cols-[max-content_1fr_1fr_1fr_1fr_1fr_1fr_max-content] tw-gap-[16px] tw-items-center tw-py-[24px] tw-border-t tw-border-[rgba(0,0,0,0.1)]"
  >
    <div
      :class="{ 'tw-invisible': header }"
      class="tw-flex tw-items-center tw-justify-center tw-gap-2"
    >
      <button
        class="tw-flex tw-items-center tw-justify-center tw-w-[28px] tw-h-[28px] tw-rounded-full tw-bg-[var(--event-secondary)]"
        @click="!header && editContact()"
      >
        <icon-pin-card />
      </button>
      <button
        @click="!header && deleteContactHandler()"
        class="tw-flex tw-items-center tw-justify-center tw-w-[28px] tw-h-[28px] tw-rounded-full tw-bg-[var(--event-secondary)]"
        title="Delete Contact"
      >
        <b-icon-trash class="tw-text-[var(--my-primary)]" width="16" height="16" />
      </button>
    </div>

    <template v-if="header">
      <div class="header-title">
        {{ item.name }}
      </div>
      <div class="header-title">
        {{ item.status }}
      </div>
      <div class="header-title">{{ item.lastAction }}</div>
      <div class="header-title">{{ item.team }}</div>
      <div class="header-title">{{ item.parentTeam }}</div>
      <div class="header-title">{{ item.gParentTeam }}</div>
    </template>

    <template v-else>
      <div class="tw-text-[16px] md:tw-text-[18px] tw-text-[var(--my-black)]">
        {{ `${item.first_name} ${item.last_name}` }}
      </div>
      <div class="tw-flex">
        <status-badge :status="item.status" />
      </div>
      <div v-if="item?.last_action_date && item?.last_action_taken">
        {{ formatDate(item?.last_action_date) }}
        {{
          $t(
            ACTION_TYPES[item?.last_action_taken].nameKey,
            ACTION_TYPES[item?.last_action_taken].nameDefault
          )
        }}
      </div>
      <div v-else>-</div>
      <div>
        {{ item.team_name || "-" }}
        <span
          v-if="linkedToOtherTeamlist.length"
          v-b-tooltip.hover.top
          tabindex="0"
          :title="
            $t('template.linked_to_other_team_list', 'contact is found in more than 1 team: ') +
            linkedToOtherTeamlistTooltip
          "
        >
          <b-icon icon="info-circle-fill" class="tw-text-orange-400 tw-cursor-pointer" />
        </span>
      </div>
      <div>{{ item.parent_team_name || "-" }}</div>
      <div>{{ item.grandparent_team_name || "-" }}</div>
    </template>

    <router-link
      :to="!header ? `/my-team-contacts/${item.id}` : ''"
      class="tw-flex tw-items-center tw-justify-center tw-w-[34px] tw-h-[34px] tw-text-[var(--my-text-color)] hover:tw-text-[var(--my-primary)] tw-transition-colors"
      :class="{ 'tw-invisible': header }"
    >
      <icon-arrow-right />
    </router-link>
  </div>
</template>

<script setup>
import statusBadge from "@/views/my-contacts/ui/my-status-badge.vue";
import iconArrowRight from "@/views/my-contacts/icons/arrow-right.vue";
import iconPinCard from "@/views/event-page/icons/pin-card.vue";
import { notificationsMixin } from "@/mixins";
import { useTeamLeaderContacts } from "@/composables/useDashboardTeamLeaderContacts";
import { computed, getCurrentInstance } from "vue";
import { actions as ACTION_TYPES } from "@/views/my-contacts/constants";

const props = defineProps({
  item: {
    type: Object,
    default: () => ({}),
  },
  header: {
    type: Boolean,
    default: false,
  },
});

const linkedToOtherTeamlist = computed(() => props.item.linked_to_other_team_list || []);

const linkedToOtherTeamlistTooltip = computed(() => {
  if (!linkedToOtherTeamlist.value.length) return "";
  return linkedToOtherTeamlist.value
    .map((team) => `(${team.team_id}) ${team.team_name}`)
    .join(", ");
});

const vm = getCurrentInstance();
const { fetchContactById, deleteContact, fetchContacts, campaignId, teamId } =
  useTeamLeaderContacts();

const notificationsMixinHandleCatch =
  notificationsMixin.methods.$_notificationsMixin_handleCatch.bind(vm.proxy);

const editContact = () => {
  fetchContactById(props.item.id)
    .then((data) => {
      vm.proxy.$root.$emit("bv::show::modal", "my-add-contact-popup");
      vm.proxy.$root.$emit("my-add-contact-popup-data", { edit: true, data });
    })
    .catch((err) => {
      notificationsMixinHandleCatch(err);
    });
};

const deleteContactHandler = async () => {
  if (!window.confirm("Are you sure you want to delete this contact?")) return;

  deleteContact(props.item.id)
    .then(() => {
      fetchContacts({
        campaign_id: campaignId.value,
        team_id: teamId.value,
      });
    })
    .catch((err) => {
      notificationsMixinHandleCatch(err);
    });
};

const formatDate = (dateStr) => {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  return d.toLocaleString();
};
</script>

<style lang="scss" scoped>
.first {
  @media screen and (max-width: 767px) {
    border-top: none;
  }
}

.header-title {
  @apply tw-text-[16px] md:tw-text-[18px] tw-font-semibold tw-text-[var(--my-black)] tw-capitalize;
}
</style>
